<template>
  <div>
    <div v-if="!$vuetify.display.mobile" class="text-center mb-6">
      <img src="https://www.cassems.com.br/images/logo.webp" alt="CASSEMS Logo" width="180" />
      <h1 class="mt-4 text-h4 font-weight-regular">
        Solicitação de Reembolso
      </h1>
    </div>

    <v-overlay v-model="tutorial.ativo" :target="tutorial.target" :scrim="false" location-strategy="connected"
      :location="tutorial.passoAtual?.posicao" offset="10" persistent no-click-animation style="z-index: 9999;">
      <TutorialCard v-if="tutorial.passoAtual" v-bind="tutorial.passoAtual" :eh-primeiro-passo="tutorial.index === 0"
        :eh-ultimo-passo="tutorial.index === tutorial.passosDaEtapa.length - 1" @proximo="proximoPassoTutorial"
        @anterior="passoAnteriorTutorial" @close="finalizarTutorial" />
    </v-overlay>

    <v-snackbar v-model="snackbar.ativo" :color="snackbar.cor" :timeout="4000" location="top">
      {{ snackbar.mensagem }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.ativo = false">Fechar</v-btn>
      </template>
    </v-snackbar>

    <v-form @submit.prevent="enviarFormulario">
      <v-stepper ref="refCabecalhoStepper" :model-value="etapaAtual" @update:model-value="irParaEtapa"
        :alt-labels="!ehCelular" :flat="$vuetify.display.mobile" :class="$vuetify.display.mobile ? '' : 'elevation-2'">

        <v-stepper-header>
          <v-stepper-item :value="1" title="Beneficiário" data-tutorial-id="etapa-beneficiario"
            :editable="etapaMaxima >= 1 && etapaAtual !== 5" :complete="etapaAtual > 1"
            :color="etapaAtual === 1 ? 'primary' : undefined" />
          <v-divider />
          <v-stepper-item :value="2" title="Despesas" data-tutorial-id="etapa-despesas"
            :editable="etapaMaxima >= 2 && etapaAtual !== 5" :complete="etapaAtual > 2"
            :color="etapaAtual === 2 ? 'primary' : undefined" />
          <v-divider />
          <v-stepper-item :value="3" title="Documentos" data-tutorial-id="etapa-documentos"
            :editable="etapaMaxima >= 3 && etapaAtual !== 5" :complete="etapaAtual > 3"
            :color="etapaAtual === 3 ? 'primary' : undefined" />
          <v-divider />
          <v-stepper-item :value="4" title="Dados Bancários" data-tutorial-id="etapa-bancarios"
            :editable="etapaMaxima >= 4 && etapaAtual !== 5" :complete="etapaAtual > 4"
            :color="etapaAtual === 4 ? 'primary' : undefined" />
          <v-divider />
          <v-stepper-item :value="5" title="Protocolo" data-tutorial-id="etapa-protocolo"
            :color="etapaAtual === 5 ? 'primary' : undefined" />
        </v-stepper-header>

        <v-toolbar v-if="$vuetify.display.mobile" flat>
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">
            Solicitação de Reembolso
          </v-toolbar-title>
        </v-toolbar>

        <v-alert v-if="$vuetify.display.mobile && mensagemInformativaEtapa" :text="mensagemInformativaEtapa" type="info"
          variant="tonal" density="compact" class="text-caption mx-4 mt-2" rounded="lg">
        </v-alert>

        <v-stepper-window>
          <v-stepper-window-item :value="1">
            <BeneficiarioStep ref="beneficiarioStepRef" v-model="dadosFormulario" :titular="props.titular" :familiares="props.familiares"
              :familiares-loading="props.familiaresLoading" :error="errosFormulario.beneficiario"
              @update:beneficiario-selecionado="atualizarBeneficiario" v-model:atendimento="dadosFormulario.atendimento"
              :solicitacoes="solicitacoes" :solicitacoes-loading="solicitacoesLoading" />
          </v-stepper-window-item>

          <v-stepper-window-item :value="2">
            <DespesasStep v-model="dadosFormulario" :form-errors="errosFormulario" :reimbursement-types="tiposReembolso"
              :reimbursement-types-loading="reimbursementTypesLoading" />
          </v-stepper-window-item>

          <v-stepper-window-item :value="3">
            <DocumentosStep v-model:listaDocumentos="listaDocumentos" v-model:justificativa="justificativa"
              :mostrar-erro-documentos="mostrarErroDocumentos" :mostrar-erro-justificativa="mostrarErroJustificativa" />
          </v-stepper-window-item>

          <v-stepper-window-item :value="4">
            <DadosBancariosStep ref="dadosBancariosStepRef" v-model="dadosFormulario"
              v-model:formatted-bank-account="contaBancariaFormatada" v-model:show-manual="exibeEntradaManual"
              :form-errors="errosFormulario" :banks="banks" :agencies="agencies" :banks-loading="banksLoading"
              :agencies-loading="agenciesLoading" :submission-error="erroSubmissao" />
          </v-stepper-window-item>

          <v-stepper-window-item :value="5">
            <ProtocoloStep 
              :protocol-number="dadosFormulario.protocolNumber"
              :request-status="dadosFormulario.requestStatus"
              :mensagem-aviso="prazoExpirado ? 'O limite para Solicitação de Reembolso é de 365 dias após a despesa médica.' : ''"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>

      <div class="d-flex justify-space-between align-center pa-4">
        <v-btn :disabled="etapaAtual === 1 || etapaAtual === 5" @click="etapaAnterior">
          Voltar
        </v-btn>

        <div class="d-flex align-center">
          <v-btn v-if="etapaAtual < 5" icon="mdi-help-circle" color="primary" variant="text" class="mr-2"
            @click="iniciarTutorial"></v-btn>

          <v-btn v-if="etapaAtual < 4" @click="proximaEtapa"> Próximo </v-btn>

          <v-btn v-if="etapaAtual === 4" color="primary" @click="enviarFormulario" :loading="enviando">
            Enviar Solicitação
          </v-btn>

          <v-btn v-if="etapaAtual === 5" color="primary" @click="abreMinhasSolicitacoes">
            Minhas solicitações
          </v-btn>

          <v-btn v-if="etapaAtual === 5" color="primary" @click="fecharFluxo" class="ml-2">
            Fechar
          </v-btn>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { useQuery } from '@vue/apollo-composable';
import { gql } from '@apollo/client';

import BeneficiarioStep from '@/components/reembolso/BeneficiarioStep.vue';
import DespesasStep from '@/components/reembolso/DespesasStep.vue';
import DocumentosStep from '@/components/reembolso/DocumentosStep.vue';
import DadosBancariosStep from '@/components/reembolso/DadosBancariosStep.vue';
import ProtocoloStep from '@/components/reembolso/ProtocoloStep.vue';
import { useReembolsoValidation } from './composables/useReembolsoValidation.js';
import TutorialCard from './components/shared/TutorialCard.vue';
import { passosTutorialReembolso } from './tutorial/reembolsoTutorial.js';

const props = defineProps({
  titular: { type: Object, default: () => ({}) },
  dataSolicitacao: { type: String, default: '' },
  familiares: { type: Array, default: () => [] },
  familiaresLoading: { type: Boolean, default: false },
});

const router = useRouter();
const { mobile: ehCelular } = useDisplay();
const etapaAtual = ref(1);
const refCabecalhoStepper = ref(null);
const dadosBancariosStepRef = ref(null);
const beneficiarioStepRef = ref(null);
const exibeEntradaManual = ref(false);

const snackbar = ref({
  ativo: false,
  mensagem: '',
  cor: 'error'
});

const mostrarMensagem = (msg, cor = 'error') => {
  snackbar.value.mensagem = msg;
  snackbar.value.cor = cor;
  snackbar.value.ativo = true;
};

const mensagensInformativas = {
  1: "Atenção: O reembolso será depositado exclusivamente na conta bancária do titular do plano.",
  2: "Lembre-se: a Nota Fiscal/Recibo deve conter o CPF ou CNPJ do prestador. Em caso de pessoa jurídica, deve constar também o nome e CRM do profissional que realizou o atendimento.",
  3: "Anexe os documentos solicitados. Itens marcados com * são obrigatórios para o tipo de serviço selecionado.",
  4: "Caso o reembolo esteja de acordo com as Normas da Cassems, o valor a ser restituído será de acordo com a TABELA PRATICADA PELA CASSEMS e deverá ser depositado no:",
};

const mensagemInformativaEtapa = computed(() => mensagensInformativas[etapaAtual.value] || null);
const etapaMaxima = ref(1);
const erroSubmissao = ref('');
const enviando = ref(false);

const {
  errosFormulario,
  mostrarErroDocumentos,
  mostrarErroJustificativa,
  limparErros,
  validarEtapa2,
  validarEtapa3,
  validarEtapa4
} = useReembolsoValidation();

const justificativa = ref('');

const dadosFormulario = ref({
  beneficiarioHandle: null,
  beneficiarioBenId: null,
  nome: '',
  email: '',
  eTitular: '',
  matricula: '',
  phone: '',
  endereco: '',
  cidade: '',
  estado: '',
  cpf: '',
  requestDate: '',
  expenseDate: null,
  totalValue: null,
  serviceType: null,
  invoiceNumber: '',
  providerName: '',
  providerNameManual: '',
  providerCpfCnpj: '',
  atendimento: null,
  bankId: null,
  bankAccount: '',
  agencyId: null,
  manualBankName: '',
  manualAgencyName: '',
  manualBankAccount: '',
  protocolNumber: '',
  requestStatus: '',
  titularidadeConta: 'titular',
  arquivoAutorizacao: [],
  nomeFavorecido: '',
  cpfFavorecido: '',
});

const prazoExpirado = computed(() => {
  if (!dadosFormulario.value.expenseDate) return false;
  const dataDespesa = new Date(dadosFormulario.value.expenseDate + 'T00:00:00');
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const limite = new Date(hoje);
  limite.setDate(hoje.getDate() - 365);
  return dataDespesa < limite;
});

watch(exibeEntradaManual, (manualAtivo) => {
  if (manualAtivo) {
    dadosFormulario.value.bankId = null;
    dadosFormulario.value.agencyId = null;
    dadosFormulario.value.bankAccount = '';
  } else {
    dadosFormulario.value.manualBankName = '';
    dadosFormulario.value.manualAgencyName = '';
    dadosFormulario.value.manualBankAccount = '';
    errosFormulario.value.manualBankName = '';
    errosFormulario.value.manualAgencyName = '';
    errosFormulario.value.manualBankAccount = '';
  }
});

const tutorial = ref({
  ativo: false,
  passosDaEtapa: [],
  index: 0,
  passoAtual: null,
  target: null
});

const tutorialJaVerificado = ref(false);
const isGuidedTourActive = ref(false);

const iniciarTutorial = () => {
  const passos = passosTutorialReembolso[etapaAtual.value] || [];
  if (passos.length === 0) return;

  tutorial.value = {
    ativo: true,
    passosDaEtapa: passos,
    index: 0,
    passoAtual: passos[0],
    target: null
  };
};

const finalizarTutorial = () => {
  tutorial.value.ativo = false;
  isGuidedTourActive.value = false;
};

const proximoPassoTutorial = () => {
  if (tutorial.value.index < tutorial.value.passosDaEtapa.length - 1) {
    tutorial.value.index++;
  } else {
    tutorial.value.ativo = false;
  }
};

const passoAnteriorTutorial = () => {
  if (tutorial.value.index > 0) {
    tutorial.value.index--;
  }
};

watch(() => tutorial.value.index, (novoIndex) => {
  tutorial.value.passoAtual = tutorial.value.passosDaEtapa[novoIndex];
});

watch(() => tutorial.value.passoAtual, async (passo) => {
  if (!passo || !tutorial.value.ativo) return;
  await nextTick();

  const elemento = document.querySelector(`[data-tutorial-id="${passo.chave}"]`);
  if (!elemento) {
    console.warn(`[Tutorial] Elemento não encontrado: ${passo.chave}`);
    finalizarTutorial();
    return;
  }
  tutorial.value.target = elemento;
}, { deep: true });

watch(etapaAtual, () => {
  if (tutorial.value.ativo) {
    finalizarTutorial();
  }
});

const GET_BANKS_QUERY = gql`query GetBanks { bancos { id nome codigo } }`;
const GET_AGENCIES_QUERY = gql`query GetAgencies($bancoId: ID!) { agencias(bancoId: $bancoId) { id nome codigo } }`;
const GET_REIMBURSEMENT_TYPES_QUERY = gql`query GetTiposReembolso { tiposReembolso { id descricao } }`;
const GET_DOCUMENTS_BY_REIMBURSEMENT_TYPE_QUERY = gql`
  query GetDocumentosPorReembolso($tipoReembolsoHandle: ID!) {
    documentosPorReembolso(tipoReembolsoHandle: $tipoReembolsoHandle) {
      id descricao obrigatorio
    }
  }
`;
const GET_PROTOCOLO_AUTORIZADO_QUERY = gql`
  query Solicitacoes($handleBeneficiario: String!) {
    solicitacoes(handleBeneficiario: $handleBeneficiario) {
      atendimento
      protocoloAtendimento
    }
  }
`;

const { result: banksResult, loading: banksLoading } = useQuery(GET_BANKS_QUERY);
const banks = computed(() => banksResult.value?.bancos || []);

const { result: agenciesResult, loading: agenciesLoading } = useQuery(
  GET_AGENCIES_QUERY,
  computed(() => ({ bancoId: dadosFormulario.value.bankId })),
  { enabled: computed(() => !!dadosFormulario.value.bankId) }
);
const agencies = computed(() => agenciesResult.value?.agencias || []);

const { result: reimbursementTypesResult, loading: reimbursementTypesLoading } = useQuery(GET_REIMBURSEMENT_TYPES_QUERY);
const tiposReembolso = computed(() => reimbursementTypesResult.value?.tiposReembolso || []);

const listaDocumentos = ref([]);
const { result: documentsResult } = useQuery(
  GET_DOCUMENTS_BY_REIMBURSEMENT_TYPE_QUERY,
  computed(() => ({ tipoReembolsoHandle: dadosFormulario.value.serviceType })),
  { enabled: computed(() => !!dadosFormulario.value.serviceType) }
);

const { result: solicitacoesResult, loading: solicitacoesLoading } = useQuery(
  GET_PROTOCOLO_AUTORIZADO_QUERY,
  computed(() => ({ handleBeneficiario: dadosFormulario.value.beneficiarioHandle })),
  { enabled: computed(() => !!dadosFormulario.value.beneficiarioHandle) }
);
const solicitacoes = computed(() => solicitacoesResult.value?.solicitacoes || []);

const formatarCPF = (cpf) => {
  if (!cpf) return '';
  const cleaned = String(cpf).replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatarTelefone = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('55') && (cleaned.length === 12 || cleaned.length === 13)) {
    cleaned = cleaned.substring(2);
  }
  if (cleaned.length === 11) return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (cleaned.length === 10) return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return phone;
};

const arquivoParaBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

const contaBancariaFormatada = computed({
  get: () => dadosFormulario.value.bankAccount,
  set: (novoValor) => {
    if (!novoValor) {
      dadosFormulario.value.bankAccount = '';
      return;
    }
    const valorFiltrado = String(novoValor).replace(/[^0-9xX-]/g, '');
    dadosFormulario.value.bankAccount = valorFiltrado.toUpperCase();
  },
});

const atualizarBeneficiario = (beneficiarioSelecionado) => {
  if (!beneficiarioSelecionado) return;
  const endereco = props.titular.enderecoCorrespondencia;
  let enderecoCompleto = '';
  if (endereco) {
    const parts = [endereco.logradouro, endereco.numero].filter(Boolean).join(', ');
    enderecoCompleto = [parts, endereco.bairro].filter(Boolean).join(' - ');
  }
  dadosFormulario.value.beneficiarioHandle = beneficiarioSelecionado.handle;
  dadosFormulario.value.beneficiarioBenId = beneficiarioSelecionado.benId;
  dadosFormulario.value.nome = beneficiarioSelecionado.nome;
  dadosFormulario.value.email = beneficiarioSelecionado.email;
  dadosFormulario.value.phone = formatarTelefone(beneficiarioSelecionado.celular);
  dadosFormulario.value.cpf = formatarCPF(beneficiarioSelecionado.matricula?.cpf);
  dadosFormulario.value.matricula = props.titular.cartao;
  dadosFormulario.value.endereco = enderecoCompleto;
  dadosFormulario.value.cidade = endereco?.municipio?.nome || '';
  dadosFormulario.value.estado = endereco?.estado?.sigla || '';
};

const verificarTutorialEAtualizarAcesso = async () => {
  const cpf = (dadosFormulario.value.cpf || '').replace(/\D/g, '');

  if (!cpf) return;

  try {
    const response = await fetch('https://n8n.app.cassems.com.br/webhook/protocolo-rembolso-cpf-hml', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cpf }),
    });

    const registros = await response.json();

    if (!registros || registros.length === 0) {
      isGuidedTourActive.value = true;
      iniciarTutorial();
      return;
    }

    registros.sort((a, b) => b.DATA_RECEBIMENTO.localeCompare(a.DATA_RECEBIMENTO));

    const registroMaisRecente = registros[0];
    const dataUltimaSolicitacao = new Date(registroMaisRecente.DATA_RECEBIMENTO);
    const hoje = new Date();
    const diffMeses = (hoje.getFullYear() - dataUltimaSolicitacao.getFullYear()) * 12 + (hoje.getMonth() - dataUltimaSolicitacao.getMonth());

    if (diffMeses >= 6) {
      isGuidedTourActive.value = true;
      iniciarTutorial();
    }
  } catch (error) {
    console.error('[Tour Automático] Erro ao processar acesso:', error);
  }
};

watch(() => props.titular, (novoTitular) => {
  if (novoTitular?.handle) {
    atualizarBeneficiario(novoTitular);
    if (!tutorialJaVerificado.value) {
      tutorialJaVerificado.value = true;
      verificarTutorialEAtualizarAcesso();
    }
  }
}, { immediate: true, deep: true });

watch(() => props.dataSolicitacao, (novaData) => {
  if (novaData) dadosFormulario.value.requestDate = novaData.split('T')[0];
}, { immediate: true });

watch(() => dadosFormulario.value.bankId, () => {
  if (!exibeEntradaManual.value) {
    dadosFormulario.value.agencyId = null;
    dadosFormulario.value.bankAccount = '';
  }
});

watch(documentsResult, (novosDocumentos) => {
  if (novosDocumentos?.documentosPorReembolso) {
    listaDocumentos.value = novosDocumentos.documentosPorReembolso.map(doc => ({
      id: doc.id,
      name: doc.descricao,
      required: doc.obrigatorio === 'S',
      selected: doc.obrigatorio === 'S',
      files: [],
      error: '',
    }));
  } else {
    listaDocumentos.value = [];
  }
}, { deep: true });

watch(listaDocumentos, (documentos) => {
  documentos.forEach(doc => {
    doc.error = (doc.selected && doc.required && (!doc.files || doc.files.length === 0))
      ? 'Pelo menos um arquivo é obrigatório.'
      : '';
  });
}, { deep: true });

const irParaEtapa = (etapa) => {
  if (etapaAtual.value !== 5 && etapaMaxima.value >= etapa) etapaAtual.value = etapa;
};

const proximaEtapa = async () => {
  limparErros();
  let ehValido = true;

  if (etapaAtual.value === 1) {
    if (!dadosFormulario.value.beneficiarioHandle) {
      errosFormulario.value.beneficiario = 'Selecione um beneficiário.';
      ehValido = false;
    }

    if (beneficiarioStepRef.value) {
      const stepValid = await beneficiarioStepRef.value.validate();
      if (!stepValid) {
        ehValido = false;
        mostrarMensagem('Informe o número do protocolo ou selecione "Não" caso não possua.');
      }
    }
  } else if (etapaAtual.value === 2) {
    ehValido = validarEtapa2(dadosFormulario.value);
  } else if (etapaAtual.value === 3) {
    ehValido = validarEtapa3(listaDocumentos.value, justificativa.value);
  }

  if (!ehValido) return;

  if (etapaAtual.value < 5) {
    etapaAtual.value++;
    etapaMaxima.value = Math.max(etapaMaxima.value, etapaAtual.value);
    if (isGuidedTourActive.value) {
      nextTick(() => iniciarTutorial());
    }
  }
};

const etapaAnterior = () => {
  if (etapaAtual.value > 1) etapaAtual.value--;
};

const enviarFormulario = async () => {
  limparErros();
  erroSubmissao.value = '';

  if (!validarEtapa4(dadosFormulario.value, exibeEntradaManual.value)) return;

  const arquivoTermo = Array.isArray(dadosFormulario.value.arquivoAutorizacao)
    ? dadosFormulario.value.arquivoAutorizacao[0]
    : dadosFormulario.value.arquivoAutorizacao;

  if (dadosFormulario.value.titularidadeConta === 'terceiro') {
    if (!dadosFormulario.value.nomeFavorecido?.trim()) {
      errosFormulario.value.nomeFavorecido = 'O nome do favorecido é obrigatório.';
      mostrarMensagem('Informe o nome do favorecido.');
      return;
    }
    if (!dadosFormulario.value.cpfFavorecido?.trim()) {
      errosFormulario.value.cpfFavorecido = 'O CPF do favorecido é obrigatório.';
      mostrarMensagem('Informe o CPF do favorecido.');
      return;
    }
    if (!arquivoTermo) {
      errosFormulario.value.arquivoAutorizacao = 'A autorização é obrigatória para depósito em conta de terceiro.';
      mostrarMensagem('A autorização é obrigatória para depósito em conta de terceiro.');
      return;
    }
  }

  enviando.value = true;

  try {
    const promessasArquivos = listaDocumentos.value
      .filter(doc => doc.selected && doc.files && doc.files.length > 0)
      .flatMap(doc =>
        doc.files.map(async file => {
          const conteudoBase64 = await arquivoParaBase64(file);
          return {
            NomeDocumento: file.name,
            IdentificadorTipoDocumento: Number(doc.id) || 99,
            ConteudoAnexo: conteudoBase64.split(',')[1],
          };
        })
      );

    if (dadosFormulario.value.titularidadeConta === 'terceiro' && arquivoTermo) {
      const file = arquivoTermo;
      promessasArquivos.push(
        (async () => {
          const conteudoBase64 = await arquivoParaBase64(file);
          const base64Limpo = conteudoBase64.split(',')[1];

          return {
            NomeDocumento: 'Termo de Autorização para Conta de Terceiro',
            IdentificadorTipoDocumento: 341,
            ConteudoAnexo: base64Limpo,
          };
        })()
      );
    }

    const arquivosAnexados = await Promise.all(promessasArquivos);

    const CONSTANTES_MANUAL = {
      ID_BANCO: 113,
      ID_AGENCIA: 0,
      CONTA: '0',
      DV: '0'
    };


    let dadosBancariosParaEnvio = {};
    let observacaoFinal = '';
    

    const prefixoIndeferido = prazoExpirado.value ? "Solicitação Indeferida por Decurso de Prazo. " : "";
    const justificativaUsuario = (justificativa.value || 'Solicitação de reembolso').trim();

    if (exibeEntradaManual.value) {
      const dadosManuaisTexto = `Dados bancarios:
Banco: ${dadosFormulario.value.manualBankName}
Agencia: ${dadosFormulario.value.manualAgencyName}
Conta: ${dadosFormulario.value.manualBankAccount}`;
      

      observacaoFinal = `${prefixoIndeferido}${dadosManuaisTexto}`;

      dadosBancariosParaEnvio = {
        IdentificadorBanco: CONSTANTES_MANUAL.ID_BANCO,
        IdentificadorAgencia: CONSTANTES_MANUAL.ID_AGENCIA,
        ContaCorrente: CONSTANTES_MANUAL.CONTA,
        DV: CONSTANTES_MANUAL.DV,
        NomeTitularConta: props.titular.nome,
        CPFTitularConta: (props.titular.matricula?.cpf || localStorage.getItem('cpf_reembolso') || '').replace(/\D/g, ''),
      };

    } else {
      observacaoFinal = `${prefixoIndeferido}${justificativaUsuario}`;

      const contaCompleta = dadosFormulario.value.bankAccount || '';
      const partesDV = contaCompleta.split('-');

      dadosBancariosParaEnvio = {
        IdentificadorBanco: Number(dadosFormulario.value.bankId),
        IdentificadorAgencia: Number(dadosFormulario.value.agencyId),
        ContaCorrente: partesDV.length === 2 ? partesDV[0] : contaCompleta.slice(0, -1),
        DV: partesDV.length === 2 ? partesDV[1] : contaCompleta.slice(-1),
        NomeTitularConta: props.titular.nome,
        CPFTitularConta: (props.titular.matricula?.cpf || localStorage.getItem('cpf_reembolso') || '').replace(/\D/g, ''),
      };
    }

    if (dadosFormulario.value.titularidadeConta === 'terceiro') {
      dadosBancariosParaEnvio.NomeFavorecido = dadosFormulario.value.nomeFavorecido?.trim();
      dadosBancariosParaEnvio.CPFFavorecido = (dadosFormulario.value.cpfFavorecido || '').replace(/\D/g, '');
    }

    const dadosEnvio = {
      email: props.titular.email,
      celular: (props.titular.celular || '').replace(/\D/g, ''),
      Prestador: {
        CpfCnpj: (dadosFormulario.value.providerCpfCnpj || '').replace(/\D/g, ''),
        Nome: dadosFormulario.value.providerName || dadosFormulario.value.providerNameManual,
        NomeProfissional: dadosFormulario.value.providerName || dadosFormulario.value.providerNameManual,
      },
      ValorTotalPago: dadosFormulario.value.totalValue,
      IdentificadorBeneficiario: Number(dadosFormulario.value.beneficiarioHandle),
      IdentificadorTipoReembolso: Number(dadosFormulario.value.serviceType),
      IdentificadorBeneficiarioSolicitante: Number(props.titular.handle),
      NumeroDocFiscalRecibo: dadosFormulario.value.invoiceNumber,
      DataDocumentoFiscal: new Date(dadosFormulario.value.expenseDate + 'T00:00:00').toISOString(),
      ArquivosAnexados: arquivosAnexados,
      DadosBancarios: dadosBancariosParaEnvio,
      DataRecebimento: new Date(dadosFormulario.value.requestDate).toISOString(),
      Observacao: observacaoFinal,
      NomeSolicitante: props.titular.nome,
      MatriculaCassems: String(dadosFormulario.value.matricula || ''),
      BeneficiarioAtendido: dadosFormulario.value.nome,
      Endereco: dadosFormulario.value.endereco,
      Municipio: [dadosFormulario.value.cidade, dadosFormulario.value.estado].filter(Boolean).join('/'),
      Justificativa: justificativaUsuario,
      Atendimento: dadosFormulario.value.atendimento ? Number(dadosFormulario.value.atendimento) : null,
    };

    const token = localStorage.getItem('auth_token_reembolso');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    else headers['X-CPF'] = (dadosFormulario.value.cpf || '').replace(/\D/g, '');

    const baseUrl = (process.env.VUE_APP_SOLICITACOES_API_URL || '').replace(/\/$/, '');

    const resposta = await fetch(`${baseUrl}/solicitar/reembolso`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(dadosEnvio),
    });

    const corpoResposta = await resposta.text();
    let dadosResposta = {};
    try {
      dadosResposta = JSON.parse(corpoResposta);
    } catch (e) { /* ignore */ }

    if (!resposta.ok) throw new Error(dadosResposta.mensagem || 'Erro ao processar solicitação.');

    if (dadosResposta.NumeroProtocolo) {
      dadosFormulario.value.protocolNumber = dadosResposta.NumeroProtocolo;
      dadosFormulario.value.requestStatus = 'Enviado com Sucesso';
      etapaAtual.value = 5;
      etapaMaxima.value = 5;
    } else {
      erroSubmissao.value = 'Não foi possível obter o número de protocolo.';
    }

  } catch (e) {
    console.error(e);
    erroSubmissao.value = e.message || 'Erro de conexão.';
  } finally {
    enviando.value = false;
  }
};

const abreMinhasSolicitacoes = () => router.push('/solicitacoes');

const fecharFluxo = () => {
  const targetUrl = new URL('/solicitacao-encerrada', window.location.origin);
  window.top.location.href = targetUrl.href;
};

watch(dadosFormulario, () => { erroSubmissao.value = ''; }, { deep: true });
</script>

<style scoped>
@media (max-width: 960px) {
  .v-stepper-header .v-divider {
    display: none;
  }
}
</style>